import { Box, Tab, Tabs } from "@mui/material";
import { useState, memo } from "react";

const CustomTabPanel = memo(({ children, value, index, noPadding, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      style={{
        ...other.style,
        visibility: value !== index ? 'hidden' : 'visible',
        height: value !== index ? 0 : 'auto',
        overflow: value !== index ? 'hidden' : 'auto'
      }}
    >
      <Box sx={{ p: noPadding ? 0 : 3 }}>{children}</Box>
    </div>
  );
});

const a11yProps = (index) => ({
  id: `simple-tab-${index}`,
  "aria-controls": `simple-tabpanel-${index}`,
});

const TabsGeneric = memo(({ tabs = [], complementaryHeader, value, onChange, noPadding = false, scrollable = false }) => {
  const [internalTab, setInternalTab] = useState(0);
  // Controlado (value/onChange) quando o pai precisa saber qual aba está ativa
  // (ex.: pra sincronizar com ?tab= na URL ou trocar conteúdo fora das abas) —
  // sem os dois props, comportamento idêntico ao de sempre (estado interno).
  const isControlled = value !== undefined;
  const tab = isControlled ? value : internalTab;

  const handleChangeTab = (_, newValue) => {
    if (!isControlled) setInternalTab(newValue);
    onChange?.(newValue);
  };

  const tabContainerStyle = { overflow: "auto"};
  // `scrollable`: pra listas de muitas abas (ex.: as 8 abas da Apuração ICMS), `fullWidth`
  // comprime cada rótulo até cortar o texto. Aditivo — default preserva `fullWidth`
  // centralizado pros consumidores com poucas abas (PreviaINSS, AnaliseProjetos etc.).
  const containerTabStyle = scrollable
    ? { width: "100%" }
    : {
        width: "100%",
        display: "flex",
        justifyContent: "center",
      };

  const validTabs = Array.isArray(tabs) ? tabs.filter(tab => tab?.label && tab?.tab !== null) : [];

  if (validTabs.length === 0) {
    return null;
  }

  return (
    <>
      <div style={containerTabStyle}>
        <Tabs
          value={tab}
          onChange={handleChangeTab}
          centered={!scrollable}
          variant={scrollable ? "scrollable" : "fullWidth"}
          scrollButtons={scrollable ? "auto" : undefined}
          allowScrollButtonsMobile={scrollable}
          sx={{ width: "100%" }}
        >
          {validTabs.map((tabItem, index) => (
            <Tab
              key={`tab-${index}`}
              label={tabItem.label}
              {...a11yProps(index)}
            />
          ))}
        </Tabs>
      </div>
      
      {complementaryHeader && <div>{complementaryHeader}</div>}

      {validTabs.map((tabItem, index) => (
        <CustomTabPanel
          key={`tabpanel-${index}`}
          value={tab}
          index={index}
          style={tabContainerStyle}
          noPadding={noPadding}
        >
          {tabItem.tab}
        </CustomTabPanel>
      ))}
    </>
  );
});

TabsGeneric.displayName = 'TabsGeneric';

export default TabsGeneric;

// #region Documentação

/*
    Importação:
                <TabsGeneric tabs={tabs} complementaryHeader={<ComponenteReactAleatorio/>}/>

    Uso controlado (opcional) — pai decide qual aba está ativa (ex.: sincronizar com ?tab= na URL,
    ou trocar algum conteúdo fora do próprio TabsGeneric conforme a aba ativa):
                <TabsGeneric tabs={tabs} value={abaAtiva} onChange={setAbaAtiva}/>
    Sem value/onChange, o componente gerencia a aba ativa internamente (comportamento de sempre).

    ------------------------------------------------------

    Prop tabs: 
                Array de objetos compostos por label e tab, sendo label uma string e tab o consteudo a ser renderizado.

                EXEMPLO:

                  const tabs = [
                    {
                      label: "Verbas eSocial",
                      tab: (
                        <TablesAnalizar
                          typeData="valoresEsocial"
                          data={verbasEsocial.filterData}
                          debounce={functionDebounce}
                          loading={loading}
                          complementaryHeader={
                            <div className={styles.header__verbas}>
                              <Button
                                sx={{
                                  borderColor: "#18316e",
                                  color: "#18316e",
                                  fontSize: "0.7rem",
                                  transition: "transform 0.3s ease-in-out",
                                  "&:hover": {
                                    transform: "scale(1.1)",
                                    border: "1px solid #18316e",
                                  },
                                }}
                                startIcon={<FileDownloadRoundedIcon />}
                                variant="outlined"
                                color="primary"
                                type="button"
                                onClick={() => alert("exportou")}
                              >
                                Exportar Relatório
                              </Button>
                            </div>
                          }
                        />
                      ),
                    },
                    {
                      label: "Verbas com valores",
                      tab: (
                        <TablesAnalizar
                          typeData="verbasComValores"
                          data={verbasComValores.filterData}
                          debounce={functionDebounce}
                          loading={loading}
                          complementaryHeader={
                            <div className={styles.header__verbas}>
                              <Button
                                sx={{
                                  borderColor: "#18316e",
                                  color: "#18316e",
                                  fontSize: "0.7rem",
                                  transition: "transform 0.3s ease-in-out",
                                  "&:hover": {
                                    transform: "scale(1.1)",
                                    border: "1px solid #18316e",
                                  },
                                }}
                                startIcon={<FileDownloadRoundedIcon />}
                                variant="outlined"
                                color="primary"
                                type="button"
                                onClick={() => alert("exportou")}
                              >
                                Exportar Relatório
                              </Button>
                            </div>
                          }
                        />
                      ),
                    },
                  ];

*/
